import { NextLink, Operation, Observable, Observer } from "@apollo/client";
import { setBackendError } from "../actions";
import { HeimdallManager } from "./heimdallManager";

type Request = {
    operation: Operation;
    forward: NextLink;
    observer: Observer<string>;
};

export class ExpiredAccessTokenHandler {
    private storedRequests: Request[];
    private renewInProgress: boolean;
    private readonly authManager: HeimdallManager;

    constructor(heimdallManager: HeimdallManager) {
        this.renewInProgress = false;
        this.storedRequests = [];
        this.authManager = heimdallManager;
    }

    /**
     * Renews the access token and replays the operation with it.
     *
     * Returns nothing when the token has already been renewed for this operation, so that a repeatedly expiring
     * access token cannot spin in a renew-retry loop. In that case the caller has to report the error instead.
     */
    renewToken(operation: Operation, forward: NextLink): Observable<any> | undefined {
        if (operation.getContext().accessTokenRenewed) {
            console.warn(
                "the access token has already been renewed for the %s operation, not renewing it again",
                operation.operationName,
            );
            return undefined;
        }

        operation.setContext({ accessTokenRenewed: true });

        console.debug("access token expired");
        return new Observable((observer) => {
            this.handleRequest({ operation, forward, observer }).then();
        });
    }

    private async handleRequest(request: Request) {
        if (this.renewInProgress) {
            console.debug("token renew in progress, store %s operation", request.operation.operationName);
            this.storedRequests.push(request);
            return;
        }

        this.renewInProgress = true;

        console.debug("start refreshing the access_token...");

        try {
            await this.authManager.refreshToken();
        } catch (error) {
            console.warn(`error with refreshing the access_token: ${JSON.stringify(error)}`);

            // The pending operations must not be left unresolved, the application waits for them.
            this.failRequests([request, ...this.storedRequests], error);

            if (error instanceof Error && error.message.startsWith("invalid_")) {
                this.authManager.clearAndReload();
            } else
                this.authManager.store.dispatch(
                    setBackendError({
                        code: "unknown",
                        details: typeof error === "string" ? error : JSON.stringify(error),
                    }),
                );

            return;
        } finally {
            this.renewInProgress = false;
        }

        const requests: Request[] = [request, ...this.storedRequests];
        this.storedRequests = [];

        for (const req of requests) {
            const subscriber = {
                next: req.observer.next.bind(req.observer),
                error: req.observer.error.bind(req.observer),
                complete: req.observer.complete.bind(req.observer),
            };

            this.authManager.setAuthHeaders(req.operation);
            req.forward(req.operation).subscribe(subscriber as any);
        }

        // The roles are bound to the access token and may differ for the new one, so they have to be fetched again.
        this.authManager.rolesInvalidatedEvent.trigger();
    }

    private failRequests(requests: Request[], error: unknown) {
        this.storedRequests = [];

        for (const req of requests) req.observer.error?.(error instanceof Error ? error : new Error(String(error)));
    }
}
